import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ApAgentActionComment,
  ApAgentRun,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import useAuth from "@/core/auth/use-auth";
import { dayjs } from "@/core/utils/date";
import { agentRunsKey } from "./keys";

const OPTIMISTIC_COMMENT_PREFIX = "optimistic-";

export const isOptimisticComment = (comment: ApAgentActionComment): boolean =>
  comment.id.startsWith(OPTIMISTIC_COMMENT_PREFIX);

const withComment = (
  runs: ApAgentRun[] | undefined,
  actionId: string,
  comment: ApAgentActionComment,
) =>
  runs?.map((run) => ({
    ...run,
    actions: run.actions.map((action) =>
      action.id === actionId
        ? { ...action, comments: [...(action.comments ?? []), comment] }
        : action,
    ),
  }));

const withoutComment = (
  runs: ApAgentRun[] | undefined,
  actionId: string,
  commentId: string,
) =>
  runs?.map((run) => ({
    ...run,
    actions: run.actions.map((action) =>
      action.id === actionId
        ? {
            ...action,
            comments: (action.comments ?? []).filter((c) => c.id !== commentId),
          }
        : action,
    ),
  }));

export const useCreateAgentActionComment = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (vars: { actionId: string; content: string }) =>
      api
        .post<ApAgentActionComment>(
          `/businesses/${businessId}/ap/agent_action_comments`,
          {
            agent_action_id: vars.actionId,
            comment: { content: vars.content },
          },
        )
        .then((response) => response.data),
    onMutate: async (vars) => {
      const key = agentRunsKey(businessId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ApAgentRun[]>({
        queryKey: key,
      });

      const authorName = user
        ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || null
        : null;
      const now = dayjs().toISOString();
      const optimistic: ApAgentActionComment = {
        id: `${OPTIMISTIC_COMMENT_PREFIX}${crypto.randomUUID()}`,
        agent_action_id: vars.actionId,
        content: vars.content,
        author: authorName,
        can_delete: true,
        created_at: now,
        updated_at: now,
      };
      queryClient.setQueriesData<ApAgentRun[]>({ queryKey: key }, (runs) =>
        withComment(runs, vars.actionId, optimistic),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
    },
  });
};

export const useDeleteAgentActionComment = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { actionId: string; commentId: string }) =>
      api
        .delete(
          `/businesses/${businessId}/ap/agent_action_comments/${vars.commentId}`,
        )
        .then((response) => response.data),
    onMutate: async (vars) => {
      const key = agentRunsKey(businessId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ApAgentRun[]>({
        queryKey: key,
      });
      queryClient.setQueriesData<ApAgentRun[]>({ queryKey: key }, (runs) =>
        withoutComment(runs, vars.actionId, vars.commentId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
    },
  });
};
